import RemoteIconV2 from '@/components/Icon/RemoteIconV2';
import type {AutoDeliveryFrequency} from '@/consts/icc/iccAutoDeliveryStatusTypes';
import appContent from '@/services/content/appContent.typed';
import stringUtils from '@/utils/stringUtils';
import {twx} from '@/utils/tailwindUtils';
import {getAutoDeliveryDcxFrequencyLabelFromAppContent} from '@/views/AutoDeliveryDcx/helpers/autoDeliveryDcxFrequency.helpers';

export const AUTO_DELIVERY_DCX_ORDER_FREQUENCY_VARIANTS = {
    STACKED: 'stacked',
    COMPACT: 'compact',
} as const;

export type AutoDeliveryDcxOrderFrequencyVariant =
    (typeof AUTO_DELIVERY_DCX_ORDER_FREQUENCY_VARIANTS)[keyof typeof AUTO_DELIVERY_DCX_ORDER_FREQUENCY_VARIANTS];

interface AutoDeliveryDcxOrderFrequencyProps {
    frequency?: AutoDeliveryFrequency;
    minDeliveryDaysCount?: number;
    maxDeliveryDaysCount?: number;
    variant?: AutoDeliveryDcxOrderFrequencyVariant;
}

const AutoDeliveryDcxOrderFrequency = (props: AutoDeliveryDcxOrderFrequencyProps) => {
    const {
        frequency,
        minDeliveryDaysCount,
        maxDeliveryDaysCount,
        variant = AUTO_DELIVERY_DCX_ORDER_FREQUENCY_VARIANTS.STACKED,
    } = props;
    const {orderFrequencyTitle, orderFrequencyIcon, orderFrequencyDescription} =
        appContent.getAutoDeliveryContent().dashboardContent;
    const frequencyLabel = frequency ? getAutoDeliveryDcxFrequencyLabelFromAppContent(frequency) : undefined;
    const formattedOrderFrequencyDescription = stringUtils.formatString(
        orderFrequencyDescription,
        minDeliveryDaysCount,
        maxDeliveryDaysCount
    );
    const hasFrequencyTitleAndLabel = orderFrequencyTitle && frequencyLabel;

    return (
        <div
            className={twx(
                'flex flex-col gap-1',
                variant === AUTO_DELIVERY_DCX_ORDER_FREQUENCY_VARIANTS.COMPACT && 'gap-4'
            )}
        >
            {hasFrequencyTitleAndLabel && variant === AUTO_DELIVERY_DCX_ORDER_FREQUENCY_VARIANTS.STACKED && (
                <>
                    <span className='text-14 leading-[20px]'>{orderFrequencyTitle}</span>
                    <span className='text-20 font-bold leading-[28px]'>{frequencyLabel}</span>
                </>
            )}
            {formattedOrderFrequencyDescription && (
                <div className='flex items-center gap-1'>
                    <RemoteIconV2 icon={orderFrequencyIcon} className='size-6' />
                    <span className='text-12 leading-[16px]'>{formattedOrderFrequencyDescription}</span>
                </div>
            )}
            {hasFrequencyTitleAndLabel && variant === AUTO_DELIVERY_DCX_ORDER_FREQUENCY_VARIANTS.COMPACT && (
                <div className='text-14 leading-[20px] tracking-04'>
                    {orderFrequencyTitle}: <b className='tracking-normal'>{frequencyLabel}</b>
                </div>
            )}
        </div>
    );
};

export default AutoDeliveryDcxOrderFrequency;
